import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Volume2 } from "lucide-react";

const LETTERS = [
  { letter: "A", word: "Apple", emoji: "🍎", color: "bg-brand-red text-white" },
  { letter: "B", word: "Banana", emoji: "🍌", color: "bg-brand-yellow" },
  { letter: "C", word: "Cat", emoji: "🐱", color: "bg-brand-blue" },
  { letter: "D", word: "Duck", emoji: "🦆", color: "bg-brand-green text-white" },
  { letter: "E", word: "Elephant", emoji: "🐘", color: "bg-brand-purple" },
  { letter: "F", word: "Fish", emoji: "🐟", color: "bg-brand-red text-white" },
  { letter: "G", word: "Goat", emoji: "🐐", color: "bg-brand-yellow" },
  { letter: "H", word: "Hat", emoji: "🎩", color: "bg-brand-blue" },
  { letter: "I", word: "Ice", emoji: "🧊", color: "bg-brand-green text-white" },
  { letter: "J", word: "Jam", emoji: "🍯", color: "bg-brand-purple" },
  { letter: "K", word: "Kite", emoji: "🪁", color: "bg-brand-red text-white" },
  { letter: "L", word: "Lion", emoji: "🦁", color: "bg-brand-yellow" },
  { letter: "M", word: "Monkey", emoji: "🐵", color: "bg-brand-blue" },
  { letter: "N", word: "Nest", emoji: "🪺", color: "bg-brand-green text-white" },
  { letter: "O", word: "Orange", emoji: "🍊", color: "bg-brand-purple" },
  { letter: "P", word: "Pear", emoji: "🍐", color: "bg-brand-red text-white" },
  { letter: "Q", word: "Queen", emoji: "👸", color: "bg-brand-yellow" },
  { letter: "R", word: "Rabbit", emoji: "🐰", color: "bg-brand-blue" },
  { letter: "S", word: "Star", emoji: "⭐", color: "bg-brand-green text-white" },
  { letter: "T", word: "Tree", emoji: "🌳", color: "bg-brand-purple" },
  { letter: "U", word: "Umbrella", emoji: "☂️", color: "bg-brand-red text-white" },
  { letter: "V", word: "Van", emoji: "🚐", color: "bg-brand-yellow" },
  { letter: "W", word: "Watermelon", emoji: "🍉", color: "bg-brand-blue" },
  { letter: "X", word: "Xylophone", emoji: "🎹", color: "bg-brand-green text-white" },
  { letter: "Y", word: "Yo-yo", emoji: "🪀", color: "bg-brand-purple" },
  { letter: "Z", word: "Zebra", emoji: "🦓", color: "bg-brand-red text-white" },
];

function speak(text, lang = "en-US") {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang;
  u.rate = 0.85;
  u.pitch = 1.1;
  window.speechSynthesis.speak(u);
}

export default function AlphabetGame() {
  const [active, setActive] = useState(null);

  const play = useCallback((item) => {
    setActive(item.letter);
    speak(`${item.letter}. ${item.letter} is for ${item.word}.`);
    setTimeout(() => setActive(null), 900);
  }, []);

  return (
    <main className="mx-auto max-w-6xl px-6 py-10" data-testid="alphabet-game">
      <Link to="/games" className="inline-flex items-center gap-2 text-brand-ink/80 hover:text-brand-red font-bold">
        <ArrowLeft size={18} /> All games
      </Link>

      <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-brand-ink">Alphabet & Phonics</h1>
          <p className="mt-2 text-brand-ink/80 text-lg">Tap a letter to hear it. Then say it out loud together!</p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-white px-4 py-2 shadow-brutal-sm">
          <Volume2 size={18} /> <span className="text-sm font-bold">Turn on your sound</span>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
        {LETTERS.map((l) => (
          <button
            key={l.letter}
            onClick={() => play(l)}
            data-testid={`letter-${l.letter}`}
            className={`relative rounded-2xl border-[3px] border-brand-ink ${l.color} p-3 shadow-brutal-sm lift-hover aspect-square flex flex-col items-center justify-center ${active === l.letter ? "animate-pop" : ""}`}
          >
            <span className="font-display text-4xl md:text-5xl font-bold leading-none">{l.letter}</span>
            <span className="text-2xl mt-1" aria-hidden>{l.emoji}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${l.color.includes("text-white") ? "text-white/85" : "text-brand-ink/70"}`}>{l.word}</span>
          </button>
        ))}
      </div>
    </main>
  );
}
